import * as React from "react";
import { motion } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { CheckCircle2 } from "lucide-react";
import { cn } from "../lib/cn";
import { springs, easings, durations } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { celebrate, type CelebrateIntensity } from "../lib/celebrate";

/**
 * SuccessState - for "you did it" confirmation surfaces (reset-password done,
 * email verified, invite accepted). The third member of the
 * EmptyState / ErrorState set: same prop names, same wrapper skeleton, same
 * 0.24s fade+lift, so a screen can swap `ErrorState` -> `SuccessState` by
 * changing the tag.
 *
 * AIKO voice defaults: short, owns the win, points the user onward without
 * gloating. Override `title` / `description` per surface (reset-password
 * passes "You can now log in with your new password.").
 *
 * Composition:
 * - `icon` - defaults to `CheckCircle2` in a success-tinted medallion that
 *   pops in with a light overshoot (the "confirmation pop")
 * - a ping halo `<span>` behind the medallion (a soft pulse outward)
 * - `title` - short heading (default: "All set.")
 * - `description` - one line (default: "That worked. You can carry on from here.")
 * - `action` - the single CTA slot (consumer passes a `Button` / `Link`; the
 *   primitive never imports a router, so the same component serves
 *   reset-password -> /login and verify-email -> wherever)
 * - `celebrate` - opt-in confetti for the big-moment screens, via the in-repo
 *   `celebrate()` helper (off by default so list / inline successes stay quiet)
 *
 * Accessibility: the wrapper is `aria-live="polite"` (success is a status, not
 * an `alert`), the title is a semantic `h3`, and focus styling is inherited
 * from whatever `action` you pass.
 *
 * Motion honors `prefers-reduced-motion` via `useReducedMotion()`: when the
 * user prefers reduced motion the wrapper, medallion, and halo render at their
 * final state with no animation, and `celebrate()` no-ops on its own. `null`
 * (pre-resolve) is treated as "allowed" to avoid an animate-then-snap flash.
 *
 *   <SuccessState
 *     title="Password reset successful."
 *     description="You can now log in with your new password."
 *     action={<Button onClick={() => navigate("/login")}>Go to sign in</Button>}
 *     celebrate
 *   />
 */

/**
 * Tone seam. Ships `success` only today (a success-tinted medallion via the
 * `--success` brand token, mirroring how `ErrorState` uses `--destructive`);
 * the cva keeps the seam open for a second tint later without an API break.
 */
const successMedallionVariants = cva(
  "relative flex items-center justify-center w-14 h-14 rounded-full",
  {
    variants: {
      tone: {
        success: "bg-success/10 text-success",
      },
    },
    defaultVariants: { tone: "success" },
  },
);

type SuccessTone = NonNullable<VariantProps<typeof successMedallionVariants>["tone"]>;

const HALO_TONE: Record<SuccessTone, string> = {
  success: "bg-success/20",
};

export interface SuccessStateProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title" | "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag">,
    VariantProps<typeof successMedallionVariants> {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** The single CTA slot. Pass a `Button` / `Link`; the primitive never navigates itself. */
  action?: React.ReactNode;
  /** When true, animates in on mount via Framer (subtle fade + lift, scale-in medallion, looped halo). */
  animate?: boolean;
  /**
   * Opt-in confetti for the big-moment screens. `true` maps to `"burst"`; pass
   * an intensity for finer control. Fires once on mount through the in-repo
   * `celebrate()` helper, which self-guards SSR and `prefers-reduced-motion`.
   */
  celebrate?: boolean | CelebrateIntensity;
}

const DEFAULT_TITLE = "All set.";
const DEFAULT_DESCRIPTION = "That worked. You can carry on from here.";

export const SuccessState = React.forwardRef<HTMLDivElement, SuccessStateProps>(
  (
    {
      className,
      icon,
      title = DEFAULT_TITLE,
      description = DEFAULT_DESCRIPTION,
      action,
      animate = true,
      celebrate: celebrateProp = false,
      tone = "success",
      ...props
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    // Treat `null` (pre-resolve) as allowed, per the hook's documented guidance,
    // so SSR content doesn't animate-then-snap once the client resolves.
    const motionAllowed = reduced === false || reduced === null;
    const animated = animate && motionAllowed;

    // Fire confetti once on mount when opted in. `celebrate()` self-guards SSR
    // and reduced-motion (canvas-confetti's `disableForReducedMotion`), so this
    // effect needs no extra gating beyond the opt-in flag.
    React.useEffect(() => {
      if (!celebrateProp) return;
      const intensity: CelebrateIntensity =
        celebrateProp === true ? "burst" : celebrateProp;
      celebrate({ intensity });
    }, [celebrateProp]);

    const resolvedTone: SuccessTone = tone ?? "success";
    const renderedIcon = icon ?? <CheckCircle2 className="h-6 w-6" />;

    const wrapperClass = cn(
      "flex flex-col items-center justify-center text-center px-6 py-12 gap-4",
      "rounded-2xl border border-success/30 bg-success/5",
      className,
    );

    const medallion = (
      <>
        {/* Ping halo - a soft pulse outward behind the medallion. Only rendered
            (and looped) when motion is allowed; reduced-motion drops it. */}
        {animated ? (
          <motion.span
            aria-hidden
            className={cn("absolute inset-0 rounded-full", HALO_TONE[resolvedTone])}
            initial={{ scale: 1, opacity: 0.35 }}
            animate={{ scale: [1, 1.6], opacity: [0.35, 0] }}
            transition={{
              duration: durations.surface * 5,
              ease: easings.smoothOut,
              repeat: Infinity,
            }}
          />
        ) : null}
        {renderedIcon}
      </>
    );

    const inner = (
      <>
        {animated ? (
          <motion.div
            className={successMedallionVariants({ tone })}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={springs.bouncy}
          >
            {medallion}
          </motion.div>
        ) : (
          <div className={successMedallionVariants({ tone })}>{renderedIcon}</div>
        )}
        <div className="space-y-1.5 max-w-sm">
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {action ? <div className="pt-1">{action}</div> : null}
      </>
    );

    if (animated) {
      return (
        <motion.div
          ref={ref}
          role="status"
          aria-live="polite"
          className={wrapperClass}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: durations.surface, ease: easings.smoothOut }}
        >
          {inner}
        </motion.div>
      );
    }

    return (
      <div ref={ref} role="status" aria-live="polite" className={wrapperClass} {...props}>
        {inner}
      </div>
    );
  },
);
SuccessState.displayName = "SuccessState";
